/* eslint-disable react/prop-types */

import { Dialog, DialogContent, DialogTitle, DialogActions, Button, Box, Typography, Paper } from "@material-ui/core";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import RadioButtonUncheckedIcon from "@material-ui/icons/RadioButtonUnchecked";
import MuiAlert from "@material-ui/lab/Alert";
import i18n from "i18next";
import React, { useState } from "react";
import lod_ from "lodash";
import { useDispatch, useSelector } from "react-redux";
import cockpitActions from "../../../redux/actions/cockpitActions";
import { replaceInTextHbr } from "../../utilities";

/**
 * Modal for the "updatePathContext" actionFront (version 2.0).
 *
 * Displays the list located at `config.pathPayloadArray` (path resolved on the
 * selected conversation). The agent selects a single element ; on validation an
 * actionFront `updateConvContextPartial` is dispatched to write the selected
 * object into the context at `config.pathContextTarget`.
 *
 * @param {object} props
 * @param {object} props.actionObject The action definition (config inside)
 * @param {Function} props.handleClose Close the modal
 */
function UpdatePathContextDialog({ actionObject, handleClose }) {
	const { selectedConversation } = useSelector(state => state.cockpit);
	const dispatch = useDispatch();

	const config = actionObject?.config ?? {};
	const { pathPayloadArray, pathContextTarget, mappingLabel, nextActionsFront } = config;

	const configError =
		lod_.isEmpty(pathPayloadArray) || lod_.isEmpty(pathContextTarget) ? true : false;

	const list = configError ? [] : lod_.get(selectedConversation, pathPayloadArray);
	const isValidList = !configError && Array.isArray(list) && !lod_.isEmpty(list);

	const [selectedIndex, setSelectedIndex] = useState(null);
	const [selectedValue, setSelectedValue] = useState({});
	const [error, setError] = useState(null);

	const getItemLabel = (item, index) => {
		if (mappingLabel) {
			const label = replaceInTextHbr(mappingLabel, item);
			if (!lod_.isNil(label) && label !== "") return label;
		}
		return `#${index + 1}`;
	};

	const handleValidate = () => {
		const selected = lod_.cloneDeep(selectedValue);

		if (lod_.isEmpty(selected)) {
			setError(i18n.t("TabAction.updatePathContext.selectionError"));
			return;
		}

		const data = {
			header: {
				assistantID: selectedConversation?.header?.assistantID,
				fID: selectedConversation?.header?.fID
			},
			payload: {
				[pathContextTarget]: selected
			}
		};

		try {
			dispatch(
				cockpitActions.launchActionFrontV2("updateConvContextPartial", data, nextActionsFront)
			);
			handleClose();
		} catch {
			setError(i18n.t("TabAction.updatePathContext.errorConfig"));
		}
	};

	const handleSelect = (idx, item) => {
		if (lod_.isEmpty(item)) {
			setError(i18n.t("TabAction.updatePathContext.selectionError"));
			return;
		}
		setError(null);
		setSelectedIndex(idx);
		setSelectedValue(item);
	};

	const canValidate =
		isValidList && !lod_.isEmpty(selectedValue) && !lod_.isEmpty(pathContextTarget);

	return (
		<Dialog
			fullWidth
			maxWidth="sm"
			open
			onClose={handleClose}
			aria-labelledby="update-path-context-title"
		>
			<DialogTitle id="update-path-context-title">
				{i18n.t("TabAction.updatePathContext.title")}
			</DialogTitle>
			<DialogContent dividers>
				{!isValidList ? (
					<Box display="flex" justifyContent="center" padding={2}>
						<Typography>
							{config.emptyMessage || i18n.t("TabAction.updatePathContext.emptyList")}
						</Typography>
					</Box>
				) : (
					<Box display="flex" flexDirection="column" style={{ gap: 8 }}>
						{list.map((item, index) => {
							const isSelected = selectedIndex === index;
							return (
								<Paper
									key={index}
									elevation={0}
									onClick={() => handleSelect(index, item)}
									style={{
										display: "flex",
										alignItems: "center",
										gap: 12,
										padding: "12px 16px",
										cursor: "pointer",
										border: `1px solid ${isSelected ? "#1976d2" : "#e0e0e0"}`,
										borderRadius: 8,
										backgroundColor: isSelected ? "rgba(25, 118, 210, 0.08)" : "#fff",
										transition: "border-color 0.15s, background-color 0.15s"
									}}
								>
									{isSelected ? (
										<CheckCircleIcon style={{ color: "#1976d2" }} />
									) : (
										<RadioButtonUncheckedIcon style={{ color: "#bdbdbd" }} />
									)}
									<Typography
										style={{ fontWeight: isSelected ? 600 : 400, color: "rgba(0, 0, 0, 0.87)" }}
									>
										{getItemLabel(item, index)}
									</Typography>
								</Paper>
							);
						})}
					</Box>
				)}
				{error && (
					<Box mt={1}>
						<MuiAlert severity="error">{error}</MuiAlert>
					</Box>
				)}
			</DialogContent>
			<DialogActions>
				<Button onClick={handleClose} style={{ margin: "2%" }} color="secondary">
					{i18n.t("CD.cancel")}
				</Button>
				{isValidList && (
					<Button
						onClick={handleValidate}
						style={{ margin: "2%" }}
						color="primary"
						disabled={!canValidate}
					>
						{i18n.t("CD.validate")}
					</Button>
				)}
			</DialogActions>
		</Dialog>
	);
}

export default UpdatePathContextDialog;
